import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Star as StarIcon,
  Folder as FolderIcon,
  Description as FileIcon,
} from '@mui/icons-material';

interface ProfileStatsProps {
  stats: {
    totalFiles: number;
    totalFolders: number;
    totalUploads: number;
    totalDownloads: number;
    totalShares: number;
    starredFiles: number;
    storageUsed: number;
    storageLimit: number;
  };
}

const ProfileStats: React.FC<ProfileStatsProps> = ({ stats }) => {
  const theme = useTheme();

  // Ensure stats values are valid numbers
  const safeStats = {
    totalFiles: Number(stats?.totalFiles) || 0,
    totalFolders: Number(stats?.totalFolders) || 0,
    totalUploads: Number(stats?.totalUploads) || 0,
    totalDownloads: Number(stats?.totalDownloads) || 0,
    totalShares: Number(stats?.totalShares) || 0,
    starredFiles: Number(stats?.starredFiles) || 0,
    storageUsed: Number(stats?.storageUsed) || 0,
    storageLimit: Number(stats?.storageLimit) || 0,
  };

  const statItems = [
    {
      icon: <FileIcon />,
      label: 'Files',
      value: safeStats.totalFiles,
      color: theme.palette.primary.main,
    },
    {
      icon: <FolderIcon />,
      label: 'Folders',
      value: safeStats.totalFolders,
      color: theme.palette.secondary.main,
    },
    {
      icon: <UploadIcon />,
      label: 'Uploads',
      value: safeStats.totalUploads,
      color: theme.palette.success.main,
    },
    {
      icon: <DownloadIcon />,
      label: 'Downloads',
      value: safeStats.totalDownloads,
      color: theme.palette.info.main,
    },
    {
      icon: <ShareIcon />,
      label: 'Shares',
      value: safeStats.totalShares,
      color: theme.palette.warning.main,
    },
    {
      icon: <StarIcon />,
      label: 'Starred',
      value: safeStats.starredFiles,
      color: theme.palette.error.main,
    },
  ];

  const storagePercentage = safeStats.storageLimit > 0
    ? (safeStats.storageUsed / safeStats.storageLimit) * 100
    : 0;

  // Format percentage with appropriate decimal places
  const formatPercentage = (percentage: number) => {
    if (percentage === 0) return '0';
    if (percentage < 0.01) return '<0.01';
    if (percentage < 1) return percentage.toFixed(2);
    if (percentage < 10) return percentage.toFixed(1);
    return percentage.toFixed(0);
  };

  return (
    <Card elevation={2} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: 'primary.main', 
            mr: 1 
          }} />
          Activity Statistics
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statItems.map((item, index) => (
            <Grid item xs={6} sm={4} md={2} key={index}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: `${item.color}10`,
                  border: `1px solid ${item.color}20`,
                }}
              >
                <Box sx={{ color: item.color, mb: 1 }}>
                  {item.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: item.color }}>
                  {item.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Storage Overview
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Used: {formatBytes(safeStats.storageUsed)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total: {formatBytes(safeStats.storageLimit)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(storagePercentage, 100)}
            color={storagePercentage > 90 ? 'error' : storagePercentage > 70 ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body2">
              {formatPercentage(storagePercentage)}% used
            </Typography>
            <Typography variant="body2">
              {formatBytes(Math.max(0, safeStats.storageLimit - safeStats.storageUsed))} available
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default ProfileStats; 